import {
  PasswordValidationSettingsDto,
  PasswordValidationSettingsPatchDto,
  QueryFactory,
} from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  minRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { FC, useCallback, useEffect } from 'react';

import generalStyles from './SettingsBlock.module.scss';
import { TenantSettingsKeys } from './types';
import { useRegisterUnsavedChanges } from './useRegisterUnsavedChanges';
import { useEventCallback } from '@mui/material';

export const PasswordValidationSettings: FC = () => {
  const i18n = useScopedTranslation(
    'tenant_settings.password_validation_settings',
  );
  const modals = useModal();

  const {
    data: settings,
    refetch: refetch,
    isLoading: isLoading,
  } = QueryFactory.PasswordValidationSettingsQuery.useGetQuery();

  const form = useAdvancedForm<PasswordValidationSettingsDto>(
    useCallback(async (data: PasswordValidationSettingsDto) => {
      try {
        const dto: PasswordValidationSettingsPatchDto = { ...data };
        await QueryFactory.PasswordValidationSettingsClient.patch(dto);
        await refetch();
      } catch (error) {
        const errorDetails = handleSubmitFormError(error, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    settings,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );

  const { formState, reset, register, setError, clearErrors } = form;

  const onReset = useCallback(() => {
    reset(settings);
  }, [settings]);

  useEffect(() => {
    onReset();
  }, [settings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.PasswordValidation,
    form,
  );

  const atLeastOneChecked = useEventCallback(
    (_value: boolean, values: PasswordValidationSettingsDto) => {
      // ToDo: temporary commented 59985
      /*clearErrors([
        'requireUppercase',
        'requireDigit',
        'requireNonAlphanumeric',
      ]);
      return (
        values.requireDigit ||
        values.requireUppercase ||
        values.requireNonAlphanumeric
      );*/
      return true;
    },
  );

  // formState.errors.requireLowercase is always true
  const atLeastTwoCheckedError =
    formState.errors.requireDigit ||
    formState.errors.requireUppercase ||
    formState.errors.requireNonAlphanumeric;

  return (
    <Loading loading={isLoading}>
      <form onSubmit={form.handleSubmitDefault}>
        <div
          className={generalStyles.container}
          data-test-id="password-validation-settings-container"
        >
          <div className={generalStyles.header}>
            <div className={generalStyles.row}>
              <div className={generalStyles.title}>{i18n.t('title')}</div>
            </div>
            <div className={generalStyles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18n.t('reset_button_title')}
                className={generalStyles.option}
                color={ButtonColor.Primary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                submitting={formState.isSubmitting}
                className={generalStyles.option}
                title={i18n.t('submit_button_title')}
              />
            </div>
          </div>{' '}
          <Field
            title={i18n.t('required_length')}
            className={generalStyles.field}
          >
            <Input
              variant="normal"
              type="number"
              step="1"
              {...register('requiredLength', {
                ...requiredRule(),
                ...minRule(3),
                valueAsNumber: true,
              })}
              errorText={formState.errors.requiredLength?.message}
            />
          </Field>
          <CheckBox
            title={i18n.t('require_lowercase')}
            className={generalStyles.checkbox}
            {...register('requireLowercase')}
            //disabled={true}
          />
          <CheckBox
            title={i18n.t('require_uppercase')}
            className={generalStyles.checkbox}
            {...register('requireUppercase', {
              validate: atLeastOneChecked,
            })}
          />
          <CheckBox
            title={i18n.t('require_digit')}
            className={generalStyles.checkbox}
            {...register('requireDigit', {
              validate: atLeastOneChecked,
            })}
          />
          <CheckBox
            title={i18n.t('require_non_alphanumeric')}
            className={generalStyles.checkbox}
            {...register('requireNonAlphanumeric', {
              validate: atLeastOneChecked,
            })}
          />
          {atLeastTwoCheckedError && (
            <div className={generalStyles['error-text']}>
              {i18n.t('at_least_two_options_error')}
            </div>
          )}
        </div>
      </form>
    </Loading>
  );
};
